/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiFileText, FiToggleLeft, FiToggleRight, FiSave } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";


// Using colors from useUI instead of hardcoded COLORS

export default function AdminInvoiceSettings() {
  const { theme, lang, colors } = useUI();
  const isArabic = lang === "ar";
  const isDark = theme === "dark";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
console.log(tenantId);

  const [settings, setSettings] = useState([
    { id: 1, key: "header", name: isArabic ? "الهيدر الخاص بالفاتورة" : "Invoice Header", active: true },
    { id: 2, key: "footer", name: isArabic ? "الفوتر" : "Invoice Footer", active: true },
    { id: 3, key: "issueDate", name: isArabic ? "تاريخ إصدار الفاتورة" : "Issue Date", active: true },
    { id: 4, key: "showProductImages", name: isArabic ? "عرض صور المنتجات داخل الفاتورة" : "Show Product Images", active: false },
    { id: 5, key: "showPricesNextToItems", name: isArabic ? "عرض الأسعار بجانب كل منتج" : "Show Prices Next to Items", active: true },
  ]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);

  // Fetch invoice settings on component mount
  useEffect(() => {
    fetchInvoiceSettings();
  }, []);

  const fetchInvoiceSettings = async () => {
    if (!perms.viewInvoice) {
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      
   
      
      const endpoint = `${baseUrl}/InvoiceSetting/get?tenantId=${tenantId}`;
      console.log("📥 Fetching invoice settings from:", endpoint);
      
      const res = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) {
        // If 404 or no settings found, use defaults
        if (res.status === 404) {
          console.log("ℹ️ No existing invoice settings found, using defaults");
          setHasExistingSettings(false);
          return;
        }
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("📥 Invoice settings response:", data);

      // Update settings based on API response
      if (data && typeof data === 'object') {
        setHasExistingSettings(true);
        setSettings((prev) =>
          prev.map((s) => ({
            ...s,
            active: data[s.key] !== undefined ? data[s.key] : s.active,
          }))
        );
        console.log("✅ Invoice settings loaded successfully");
      }
    } catch (err) {
      console.error("❌ Error fetching invoice settings:", err);
      // Don't show error if it's just that settings don't exist yet
      if (!err.message.includes("404")) {
        Swal.fire({
          icon: "warning",
          title: isArabic ? "تحذير" : "Warning",
          text: isArabic 
            ? "فشل تحميل الإعدادات. سيتم استخدام القيم الافتراضية." 
            : "Failed to load settings. Default values will be used.",
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
        });
      }
      setHasExistingSettings(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleSetting = (id) => {
    if (!perms.editInvoice) {
      return;
    }
    setSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, active: !s.active } : s))
    );
  };

  const handleSave = async () => {
    if (!perms.editInvoice) {
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      
     
      
      // Build payload from settings
      const payload = {
        header: settings.find(s => s.key === "header")?.active || false,
        footer: settings.find(s => s.key === "footer")?.active || false,
        issueDate: settings.find(s => s.key === "issueDate")?.active || false,
        showProductImages: settings.find(s => s.key === "showProductImages")?.active || false,
        showPricesNextToItems: settings.find(s => s.key === "showPricesNextToItems")?.active || false,
      };

      console.log("📤 Saving invoice settings:", payload);
      console.log("📤 Has existing settings:", hasExistingSettings);

      // Use POST for new settings, PUT for updating existing ones
      const method = hasExistingSettings ? "PUT" : "POST";
      const endpoint = hasExistingSettings 
        ? `${baseUrl}/InvoiceSetting/update?TenantId=${tenantId}`
        : `${baseUrl}/InvoiceSetting/add?TenantId=${tenantId}`;
      
      console.log("📤 Using method:", method);
      console.log("📤 Endpoint:", endpoint);

      const res = await fetch(endpoint, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      let result = "";
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          result = await res.json();
        } catch (parseError) {
          const text = await res.text();
          result = text;
        }
      } else {
        result = await res.text();
      }

      console.log("📥 Invoice settings response status:", res.status, res.statusText);
      console.log("📥 Invoice settings response data:", result);

      if (!res.ok) {
        const errorMessage = typeof result === 'string' ? result : (result?.message || result?.error || "Failed to save");
        throw new Error(errorMessage);
      }

      // Mark as having existing settings after successful save
      setHasExistingSettings(true);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحفظ! ✅" : "Saved! ✅",
        text: isArabic 
          ? "تم حفظ إعدادات الفاتورة بنجاح" 
          : "Invoice settings saved successfully",
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("❌ Error saving invoice settings:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحفظ ❌" : "Save Failed ❌",
        text: err.message || (isArabic ? "فشل حفظ إعدادات الفاتورة" : "Failed to save invoice settings"),
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!perms.viewInvoice) {
    return <NoAccess />;
  }
  return (
    <WhatsLayout title={isArabic ? "تخصيص الفاتورة" : "Invoice Customization"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-14 transition-all duration-300 ${
          isDark ? "text-gray-100" : "text-gray-900"
        }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : colors.bg,
        }}
      >


        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
              <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                {isArabic ? "جاري التحميل..." : "Loading..."}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* ===== كروت الإعدادات ===== */}
            {perms.viewInvoice && (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {settings.map((s) => (
            <motion.div
              key={s.id}
              whileHover={{ scale: 1.03, y: -4 }}
              transition={{ type: "spring", stiffness: 250 }}
              className={`p-6 rounded-2xl flex flex-col items-center justify-between text-center transition-all duration-300 backdrop-blur-md border shadow-2xl ${
                isDark
                  ? "bg-[#1E293B]/70 border-[#334155]"
                  : "bg-white/90 border-gray-200"
              }`}
            >
              <div className="text-5xl mb-4" style={{ color: colors.gold }}>
                <FiFileText />
              </div>
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: colors.mainColor }}
              >
                {s.name}
              </h3>

              <button
                onClick={perms.editInvoice ? () => toggleSetting(s.id) : null}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-transform text-white"
                style={{
                  background: s.active
                    ? "linear-gradient(90deg, #22C55E 0%, #16A34A 100%)"
                    : "linear-gradient(90deg, #EF4444 0%, #B91C1C 100%)",
                }}
              >
                {s.active ? (
                  <>
                    <FiToggleRight size={22} />
                    {isArabic ? "مفعل" : "Enabled"}
                  </>
                ) : (
                  <>
                    <FiToggleLeft size={22} />
                    {isArabic ? "غير مفعل" : "Disabled"}
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>
        </>
        )}

            {/* ===== زر الحفظ ===== */}
            {perms.viewInvoice && (
            <div className="text-center mt-16">
              <motion.button
                whileTap={{ scale: saving ? 1 : 0.95 }}
                onClick={perms.editInvoice ? handleSave : null}
                disabled={saving}
                className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl text-white font-semibold shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: saving ? "#94a3b8" : colors.mainColor,
                  boxShadow: saving ? "none" : "0 4px 12px rgba(116,0,191,0.4)",
                }}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isArabic ? "جاري الحفظ..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <FiSave /> {isArabic ? "حفظ التغييرات" : "Save Changes"}
                  </>
                )}
              </motion.button>
            </div>
            )}
          </>
        )}
      </section>
    </WhatsLayout>
  );
}
